import React, { useCallback, useState } from 'react';
import { Expand, Footprints, HeartPulse, LucideIcon, Medal, Music, Plane, Sparkles } from 'lucide-react';
import { profileData, type MarathonPhoto, type PersonalInterest } from '../../data/profileData';
import { getLargeImageUrl, getOptimizedImage } from '../../utils/images';
import { Card, Icon, ResponsiveImage, Section } from '../ui';
import Lightbox, { LightboxImage } from '../Lightbox/Lightbox';
import './Personal.css';

const INTEREST_ICONS: Record<string, LucideIcon> = {
  music: Music,
  marathon: Footprints,
  fitness: HeartPulse,
  travel: Plane,
};

const posterFor = (path?: string) => {
  if (!path) return undefined;
  const optimized = getOptimizedImage(path);
  return optimized ? `${optimized.base}-2x.${optimized.fallback}` : path;
};

const Running: React.FC<{ interest: PersonalInterest; onOpenPhoto: (photo: MarathonPhoto) => void }> = ({
  interest,
  onOpenPhoto,
}) => {
  const gallery = interest.gallery;
  return (
    <Card className="running">
      <div className="running__intro">
        <span className="interest__icon">
          <Icon icon={Footprints} size={24} />
        </span>
        <div>
          <h3 className="running__title">{interest.title}</h3>
          <p className="running__description">{interest.description}</p>
        </div>
      </div>

      {gallery && (
        <>
          <dl className="running__stats">
            {gallery.stats.map((stat) => (
              <div key={stat.label} className="running__stat">
                <dt>{stat.label}</dt>
                <dd>{stat.icon === 'medal' ? <Icon icon={Medal} size={28} /> : stat.number}</dd>
              </div>
            ))}
          </dl>

          <blockquote className="running__motto">{gallery.motto}</blockquote>

          <ul className="running__gallery" aria-label="Race photos">
            {gallery.photos.map((photo) => (
              <li key={photo.src}>
                <button
                  type="button"
                  className="running__photo"
                  onClick={() => onOpenPhoto(photo)}
                  aria-label={`Enlarge photo: ${photo.title}`}
                >
                  <ResponsiveImage path={photo.src} alt={photo.alt} className="running__photo-img" />
                  <span className="running__photo-caption" aria-hidden="true">
                    {photo.title}
                    <Icon icon={Expand} size={14} />
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {gallery.videos?.map((video) => (
            <figure key={video.src} className="running__video">
              <video controls preload="none" poster={posterFor(video.thumbnail)} aria-label={video.title}>
                <source src={video.src} type="video/mp4" />
              </video>
              <figcaption>{video.title}</figcaption>
            </figure>
          ))}
        </>
      )}
    </Card>
  );
};

/** Beyond Work: running (with gallery), and the other interests that round out the person. */
const Personal: React.FC = () => {
  const [lightboxImage, setLightboxImage] = useState<LightboxImage | null>(null);
  const { interests } = profileData.personal;
  const running = interests.find((interest) => interest.id === 'marathon');
  const others = interests.filter((interest) => interest.id !== 'marathon');

  const openPhoto = useCallback((photo: MarathonPhoto) => {
    setLightboxImage({ src: getLargeImageUrl(photo.src), alt: photo.alt, caption: photo.title });
  }, []);
  const closePhoto = useCallback(() => setLightboxImage(null), []);

  return (
    <Section
      id="beyond-work"
      title="Beyond Work"
      eyebrow="Life outside the code"
      intro="Running keeps me disciplined and gives me the clarity I bring to complex technical problems."
      className="beyond-work"
    >
      {running && <Running interest={running} onOpenPhoto={openPhoto} />}

      <ul className="interests">
        {others.map((interest) => (
          <Card as="li" key={interest.id} className="interest">
            <span className="interest__icon">
              <Icon icon={INTEREST_ICONS[interest.id] ?? Sparkles} size={22} />
            </span>
            <h3 className="interest__title">{interest.title}</h3>
            <p className="interest__description">{interest.description}</p>
            {interest.tips && (
              <ul className="interest__tips" aria-label={`${interest.title} tips`}>
                {interest.tips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            )}
          </Card>
        ))}
      </ul>

      <Lightbox image={lightboxImage} onClose={closePhoto} />
    </Section>
  );
};

export default Personal;
